import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/pos-api";
import { draftAlreadySubmitted, networkFailure } from '@/lib/shift-journal';
import { getActiveStore } from '@/lib/scope';
import { saveHeldDraft, clearHeldDraft, lockHeldCart, releaseHeldLock } from '@/lib/hold-handoff';

const emptyDraft = () => ({ cart: [], discount: 0, customer: null, note: "", previousDebts: [], returnCredit: null });
const hasItems = (draft) => draft.cart?.length > 0 || draft.previousDebts?.length > 0;
const requestId = () => globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export default function useRefreshCart(tenantId, userId, enabled) {
  const storageKey = `pos_refresh_cart:v2:${tenantId}:${userId}:${getActiveStore()}`;
  const [initial] = useState(() => {
    if (!enabled) return null;
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey) || "null");
      if (saved?.id && draftAlreadySubmitted(tenantId, userId, saved.id)) {
        sessionStorage.removeItem(storageKey);
        return null; // Payment journal committed before a reload interrupted clearing the cart.
      }
      return saved?.version === 1 && saved.id && Array.isArray(saved.draft?.cart) && hasItems(saved.draft) ? saved : null;
    } catch { return null; }
  });
  const [posDraft, updateDraft] = useState(initial?.draft || emptyDraft);
  const current = useRef(initial);
  const recovery = useRef((navigator.onLine || initial?.draft?.holdSource) && !initial?.draft?.checkoutQueue && !initial?.draft?.checkoutSend ? initial : null);
  const inFlight = useRef(false);
  const [recovering, setRecovering] = useState(!!recovery.current);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [storageError, setStorageError] = useState(() => {
    try {
      return enabled && sessionStorage.getItem(`pos_refresh_cart:${tenantId}:${userId}`)
        ? 'Keranjang versi lama masih disimpan di perangkat, tetapi belum memiliki penanda cabang. Jangan hapus data browser; hubungi admin untuk memulihkannya ke cabang yang benar.' : '';
    } catch { return ''; }
  });
  const draftRef = useRef(posDraft);

  const setPosDraft = useCallback((value) => {
    const next = typeof value === "function" ? value(draftRef.current) : value;
    const saved = hasItems(next) ? { version: 1, id: requestId(), draft: next } : null;
    const scope = { tenant: tenantId, user: userId, store: getActiveStore() };
    // Durable backup first. A storage failure must not replace the usable cart.
    if (saved?.draft?.holdSource) {
      try { saveHeldDraft(scope, saved); }
      catch { const message = 'Cadangan keranjang gagal disimpan. Data server tetap ada; jangan bayar atau tutup halaman.'; setStorageError(message); throw new Error(message); }
    }
    if (enabled) {
      try {
        if (saved) sessionStorage.setItem(storageKey, JSON.stringify(saved));
        else sessionStorage.removeItem(storageKey);
        setStorageError("");
      } catch { setStorageError("Browser tidak dapat menyimpan keranjang. Masukkan ke Antrian sebelum refresh."); }
    }
    const previousSource = draftRef.current?.holdSource;
    if (previousSource && previousSource.id !== next.holdSource?.id) {
      try { clearHeldDraft(scope, previousSource.id); }
      catch { setStorageError('Cadangan lama belum dapat dibersihkan. Periksa antrian sebelum memanggilnya kembali.'); }
    }
    draftRef.current = next;
    current.current = saved;
    updateDraft(next);
  }, [enabled, storageKey, tenantId, userId]);

  const recover = useCallback(async () => {
    const saved = recovery.current;
    if (!saved || inFlight.current || !enabled) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    const draft = saved.draft;
    try {
      if (draft.holdSource) await lockHeldCart({ tenant: tenantId, user: userId, store: getActiveStore() }, draft.holdSource.id);
      await api.post("/holds", {
        label: `Refresh · ${draft.customer?.name || "Keranjang"}`,
        client_request_id: saved.id,
        ...(draft.holdSource ? { hold_source: draft.holdSource } : {}),
        payload: { items: draft.cart, discount: draft.discount || 0, customer: draft.customer || null, note: draft.note || "", previousDebts: draft.previousDebts || [], returnCredit: draft.returnCredit || null },
      }, { timeout: 15000 });
      // Clear the saved snapshot only after the server acknowledges the queue entry.
      setPosDraft(emptyDraft());
      recovery.current = null;
      setRecovering(false);
      toast.success("Keranjang sebelum refresh sudah masuk ke Antrian");
    } catch (cause) {
      if (networkFailure(cause)) {
        // Keep the original local draft usable when reload has no working network.
        recovery.current = null;
        setRecovering(false);
        toast.warning('Keranjang dipulihkan di perangkat; belum dikirim ke Antrian server.');
      } else setError(apiError(cause));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, [enabled, setPosDraft, tenantId, userId]);

  useEffect(() => () => {
    const source = draftRef.current?.holdSource;
    if (source) releaseHeldLock({ tenant: tenantId, user: userId, store: getActiveStore() }, source.id);
  }, [tenantId, userId]);

  useEffect(() => {
    recover();
    window.addEventListener("online", recover);
    return () => window.removeEventListener("online", recover);
  }, [recover]);

  return { posDraft, setPosDraft, draftRequestId: current.current?.id, recovering, busy, error, storageError, recover };
}
